import { useEffect } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { getHasGretaAIChatUnread } from '../../GretaAIChat.utils'
import { useGretaAIChatBalance } from '../useGretaAIChatBalance'
import { useGretaAIChatFinanceAccess } from '../useGretaAIChatFinanceAccess'
import { useGretaAIChatSource } from '../useGretaAIChatSource'

import { useGretaAIChatSessionChatActions } from './useGretaAIChatSessionChatActions'
import { useGretaAIChatSessionChats } from './useGretaAIChatSessionChats'
import { useGretaAIChatSessionConversation } from './useGretaAIChatSessionConversation'
import { useGretaAIChatSessionPanel } from './useGretaAIChatSessionPanel'
import { useGretaAIChatSessionProjects } from './useGretaAIChatSessionProjects'
import { useGretaAIChatSessionSource } from './useGretaAIChatSessionSource'
import { useGretaAIChatSubmit } from './useGretaAIChatSubmit'
import { useGretaAIChatUnreadReplies } from './useGretaAIChatUnreadReplies'

export function useGretaAIChatSession({ displayName } = EMPTY_OBJECT) {
  const {
    activeChatId,
    activeChatIdRef,
    handleOpen,
    isMinimized,
    isOpen,
    launcherRef,
    openChat,
    openNewChat,
    panel,
    panelActions,
    panelRef,
    setActiveChatId,
    view,
  } = useGretaAIChatSessionPanel()

  const balance = useGretaAIChatBalance()
  const { checkBalance, isLimitReached, isLocked } = balance

  useEffect(() => {
    checkBalance()
  }, [])

  const financeAccessState = useGretaAIChatFinanceAccess({
    isPanelShown: isOpen && !isMinimized,
  })
  const { markFinanceUsed, revokeFinanceAccess } = financeAccessState

  const {
    activeChat,
    chatListLoader,
    chats,
    chatStatusById,
    clearChatUnread,
    getIsChatGenerationSettledRecently,
    handleChatCreated,
    handleChatMissing,
    handleSelectChat,
    markChatGenerating,
    markChatSettled,
    settleChatGeneration,
  } = useGretaAIChatSessionChats({
    activeChatId,
    activeChatIdRef,
    isLocked,
    isMinimized,
    isOpen,
    markFinanceUsed,
    openChat,
    setActiveChatId,
  })
  const chatSource = useGretaAIChatSource({ activeChat, activeChatId })

  const { handleOpenPanel, handleTurnSettled } = useGretaAIChatUnreadReplies({
    activeChatId,
    checkBalance,
    clearChatUnread,
    handleOpen,
    isMinimized,
    isOpen,
    markChatSettled,
    view,
  })

  const {
    conversation,
    conversationActions,
    forgetChat,
    isReplySettling,
    messages,
    retryLastReply,
    sendMessage,
    status,
  } = useGretaAIChatSessionConversation({
    activeChatId,
    chats,
    getIsChatGenerationSettledRecently,
    handleChatCreated,
    handleChatMissing,
    handleTurnSettled,
    markChatGenerating,
    openNewChat,
    revokeFinanceAccess,
    settleChatGeneration,
  })

  const { chatAction, chatActions, discardDraftIfSubmitted, draft } =
    useGretaAIChatSessionChatActions({
      activeChatId,
      chatListLoader,
      forgetChat,
      setActiveChatId,
    })

  const { projectAction, projectActions, projects } =
    useGretaAIChatSessionProjects({
      activeChatIdRef,
      chats,
      isLocked,
      isOpen,
      openNewChat,
      panelRef,
    })

  const { handleRetry, handleSendMessage, isSendPending } =
    useGretaAIChatSubmit({
      activeChatIdRef,
      balance,
      discardDraftIfSubmitted,
      retryLastReply,
      sendMessage,
    })

  const hasUnread = getHasGretaAIChatUnread(chatStatusById, chats)

  const {
    launcher: sourceLauncher,
    messages: sourceMessages,
    source,
    onSendMessage,
  } = useGretaAIChatSessionSource({
    activeChatId,
    chats,
    chatSource,
    chatStatusById,
    financeAccessState,
    handleSendMessage,
    hasUnread,
    isLimitReached,
    isLocked,
    isSendPending,
    messages,
    openNewChat,
    status,
  })

  return {
    actions: {
      ...chatActions,
      ...conversationActions,
      ...panelActions,
      ...projectActions,
      onRetry: handleRetry,
      onSelectChat: handleSelectChat,
      onSendMessage,
    },
    chatListLoader,
    conversation: {
      ...conversation,
      displayName,
      isSendPending: isSendPending || isReplySettling,
      messages: sourceMessages,
    },
    draft,
    launcher: {
      ...sourceLauncher,
      hasUnread,
      isLocked,
      launcherRef,
      onOpen: handleOpenPanel,
    },
    panel: {
      ...panel,
      chatAction,
      chatStatusById,
      isLimitReached,
      isLocked,
      projectAction,
    },
    projects,
    source,
  }
}
